import { useTranslation } from "react-i18next";
import { useTenant } from "@/contexts/TenantContext";

interface HeroSectionProps {
  children?: React.ReactNode;
}

/**
 * Editorial hero for the tenant home page.
 *
 * Warm paper bg with subtle grain, eyebrow + big Fraunces display headline,
 * a hairline meridian, and the booking widget below as the primary object.
 * No video background, no gradient overlays, no glassmorphism.
 */
export function HeroSection({ children }: HeroSectionProps) {
  const { t } = useTranslation();
  const { tenant } = useTenant();

  const tagline = tenant?.branding?.tagline || t("landing.tagline");
  const name = tenant?.name || "RutaLink";

  return (
    <section
      className="relative bg-stone-50 paper-grain border-b hairline-strong"
      aria-label="Hero section"
    >
      <div className="relative mx-auto max-w-6xl px-6 pt-16 pb-10 md:pt-24 md:pb-14 lg:pt-28 lg:pb-16">
        {/* Eyebrow: brand stamp */}
        <div className="flex items-center gap-3 animate-fade-up">
          <span className="h-2 w-2 bg-brand" aria-hidden="true" />
          <span className="text-eyebrow uppercase text-ink-muted tabular">
            {name}
          </span>
          <span
            className="flex-1 h-px bg-hairline-strong max-w-[10rem]"
            aria-hidden="true"
          />
        </div>

        {/* Headline */}
        <h1
          className="font-display text-display-lg md:text-display-xl text-ink mt-6 md:mt-8 max-w-4xl animate-fade-up"
          style={{ animationDelay: "60ms" }}
        >
          {tagline}
        </h1>

        {/* Lede */}
        {tenant?.companyDescription && (
          <p
            className="mt-5 md:mt-6 max-w-xl text-base md:text-lg text-ink-muted leading-relaxed animate-fade-up"
            style={{ animationDelay: "120ms" }}
          >
            {tenant.companyDescription}
          </p>
        )}

        {/* Booking widget anchor */}
        {children && (
          <div
            className="mt-10 md:mt-14 animate-fade-up"
            style={{ animationDelay: "180ms" }}
          >
            {children}
          </div>
        )}
      </div>
    </section>
  );
}

export default HeroSection;
